// components/BulkOutreachModal.jsx
//
// Sales Workspace bulk outreach. Select several prioritised companies, let
// AXO prepare (or reuse) one email draft per company, edit the generated
// subject/body inline, then approve and send them together.
//
// Cost transparency is preserved: missing drafts are estimated first and the
// total is shown before any provider call. Nothing is sent until the user
// clicks the final send button; the backend still runs every per-draft
// ownership, opt-out, verification and duplicate-send check.
import React, { useEffect, useMemo, useRef, useState } from "react";
import useRevenueApi from "../hooks/useRevenueApi";
import { Dialog, DialogContent } from "../arc/components/dialog/dialog";
import { Button } from "../arc/components/button/button";
import { Checkbox } from "../arc/components/checkbox/checkbox";
import { Input } from "../arc/components/input/input";
import { Skeleton } from "../arc/components/skeleton/skeleton";
import { Textarea } from "../arc/components/textarea/textarea";

const MAX_BULK = 25;
const CONCURRENCY = 5;
// Keep the progress state on screen even when the API answers immediately
// (demo mode / cached drafts), so a click always produces visible feedback.
const MIN_BUSY_MS = 600;

function waitForProgress(startedAt) {
  const remaining = MIN_BUSY_MS - (Date.now() - startedAt);
  return remaining > 0
    ? new Promise((resolve) => setTimeout(resolve, remaining))
    : Promise.resolve();
}

const BULK_STYLES = `
  .bulk-dialog.bulk-dialog { width: min(calc(100vw - var(--space-8)), 840px); }
  .bulk-intro { font-size: 13px; line-height: 1.55; color: var(--text-muted); margin-bottom: 14px; }
  .bulk-summary { display: flex; gap: 12px; flex-wrap: wrap; align-items: center; padding: 11px 14px; margin-bottom: 14px; border-radius: var(--radius); background: var(--surface-sunken); font-size: 13px; color: var(--text); }
  .bulk-summary b { font-variant-numeric: tabular-nums; }
  .bulk-list { display: flex; flex-direction: column; gap: 12px; max-height: min(54vh, 560px); overflow-y: auto; padding-right: 2px; }
  .bulk-item { border: 1px solid var(--border); border-radius: var(--radius); padding: 14px; background: var(--surface); }
  .bulk-item.sent { border-color: var(--green-border, var(--border)); }
  .bulk-item.failed { border-color: var(--red-border, var(--border)); }
  .bulk-item-head { display: flex; align-items: flex-start; gap: 10px; }
  .bulk-item-title { min-width: 0; flex: 1; }
  .bulk-item-name { font-size: 14px; font-weight: 600; color: var(--text); line-height: 1.35; overflow-wrap: anywhere; }
  .bulk-item-meta { margin-top: 3px; display: flex; gap: 8px; align-items: center; flex-wrap: wrap; font-size: 12.5px; color: var(--text-muted); }
  .bulk-item-recipient { overflow-wrap: anywhere; }
  .bulk-tag { display: inline-flex; align-items: center; gap: 5px; padding: 2px 8px; border-radius: 9999px; background: var(--surface-sunken); font-size: 11.5px; font-weight: 500; color: var(--text); white-space: nowrap; }
  .bulk-tag::before { content: ''; width: 5px; height: 5px; border-radius: 50%; background: var(--dot, var(--text-faint)); }
  .bulk-tag.contacted { --dot: #3b82f6; }
  .bulk-tag.ready { --dot: var(--green); }
  .bulk-tag.sent { --dot: var(--green); }
  .bulk-tag.estimated { --dot: var(--amber); }
  .bulk-tag.blocked { --dot: var(--red); }
  .bulk-item-body { margin-top: 12px; display: grid; gap: 10px; }
  .bulk-field-label { display: block; margin-bottom: 4px; font-size: 11px; font-weight: 600; letter-spacing: 0.03em; text-transform: uppercase; color: var(--text-faint); }
  .bulk-note { font-size: 12.5px; line-height: 1.55; color: var(--text-muted); }
  .bulk-error { font-size: 12.5px; line-height: 1.55; color: var(--red); }
  .bulk-estimate { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; font-size: 12.5px; color: var(--text-muted); }
  .bulk-estimate b { color: var(--text); font-variant-numeric: tabular-nums; }
  .bulk-item-actions { margin-top: 10px; display: flex; gap: 8px; flex-wrap: wrap; }
  .bulk-foot { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-top: 16px; padding-top: 14px; border-top: 1px solid var(--border); }
  .bulk-foot-note { font-size: 12.5px; color: var(--text-muted); }
  .bulk-foot-actions { display: flex; gap: 8px; flex-wrap: wrap; }
  .bulk-progress { display: flex; flex-direction: column; gap: 8px; margin-bottom: 14px; padding: 11px 14px; border-radius: var(--radius); background: var(--surface-sunken); }
  .bulk-progress-row { display: flex; align-items: center; gap: 9px; font-size: 13px; font-weight: 500; color: var(--text); }
  .bulk-progress-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--accent-mid); animation: bulk-pulse 1s ease-in-out infinite; flex-shrink: 0; }
  .bulk-progress-track { position: relative; height: 4px; overflow: hidden; border-radius: 9999px; background: var(--border); }
  .bulk-progress-fill { position: absolute; inset: 0 auto 0 0; width: 38%; border-radius: inherit; background: var(--text); animation: bulk-progress 1.15s ease-in-out infinite; }
  @keyframes bulk-pulse { 0%, 100% { opacity: 0.35; } 50% { opacity: 1; } }
  @keyframes bulk-progress { 0% { transform: translateX(-110%); } 100% { transform: translateX(275%); } }
  @media (prefers-reduced-motion: reduce) { .bulk-progress-dot, .bulk-progress-fill { animation: none; } .bulk-progress-fill { width: 100%; opacity: 0.5; } }
  @media (max-width: 640px) {
    .bulk-list { max-height: none; }
    .bulk-foot { align-items: stretch; }
    .bulk-foot-actions { width: 100%; }
    .bulk-foot-actions > button { flex: 1; }
  }
`;

function errorMessage(err) {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    "Something went wrong"
  );
}

function mapLimit(items, limit, fn) {
  const results = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const index = next++;
      try {
        results[index] = { status: "fulfilled", value: await fn(items[index], index) };
      } catch (err) {
        results[index] = { status: "rejected", reason: err };
      }
    }
  };
  return Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, worker),
  ).then(() => results);
}

function emailReady(contact) {
  if (!contact) return false;
  return contact.hasValidEmail !== undefined
    ? contact.hasValidEmail
    : Boolean(contact.hasEmail);
}

function money(value) {
  return `£${Number(value || 0).toFixed(2)}`;
}

export default function BulkOutreachModal({
  open,
  opportunities = [],
  onClose,
  onSent,
  onDraftsChanged,
}) {
  const api = useRevenueApi();
  const [items, setItems] = useState([]);
  const [phase, setPhase] = useState("loading"); // loading | review | generating | sending | done
  const [summary, setSummary] = useState(null);
  const requestRef = useRef(0);
  const itemsRef = useRef(items);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // Estimates are "no cost yet" logs. If the user closes the batch without
  // generating those drafts, decline the unused estimates so they do not sit
  // in the AI request log as estimated forever (mirrors the single-draft
  // Cancel action). Drafts that were generated are already consumed.
  useEffect(() => {
    if (!open) return undefined;
    return () => {
      itemsRef.current
        .filter((item) => item.estimate?.logId && !item.draft)
        .forEach((item) => {
          api.rejectOutreachEstimate(item.estimate.logId).catch(() => {});
        });
    };
  }, [open, api]);

  const selected = useMemo(
    () => opportunities.slice(0, MAX_BULK),
    [opportunities],
  );

  useEffect(() => {
    if (!open) return undefined;
    const requestId = ++requestRef.current;
    let cancelled = false;

    const initial = selected.map((opp) => ({
      id: opp.customerCompanyId,
      companyName: opp.companyName || "Unknown company",
      opp,
      contacted: Boolean(
        opp.lastContactAt ||
          opp.touchCount > 0 ||
          (opp.pipelineStage && opp.pipelineStage !== "new"),
      ),
      contact: null,
      draft: null,
      estimate: null,
      subject: "",
      content: "",
      edited: false,
      include: true,
      error: null,
      sent: false,
      loading: true,
    }));

    setItems(initial);
    setSummary(null);
    setPhase("loading");

    (async () => {
      const results = await mapLimit(selected, CONCURRENCY, async (opp) => {
        const res = await api.getOutreachDrafts(opp.customerCompanyId);
        const drafts = res?.drafts || [];
        const contact = res?.contact || null;
        const existing = drafts.find(
          (d) => d.draftType === "outreach_email" && d.status === "draft",
        );

        let estimate = null;
        let error = null;
        if (!existing) {
          if (!emailReady(contact)) {
            error =
              contact?.email && contact.hasValidEmail === false
                ? `Stored email is not verified: ${
                    contact.emailValidation?.reason || "validation failed"
                  }`
                : "No verified email on file. Find contact details in the opportunity first.";
          } else {
            try {
              const est = await api.estimateOutreachDraft(
                opp.customerCompanyId,
                "outreach_email",
              );
              estimate = est?.estimate || null;
              if (!estimate?.logId) {
                error = "Could not get a cost estimate for this draft.";
              }
            } catch (err) {
              error = errorMessage(err);
            }
          }
        }

        return {
          id: opp.customerCompanyId,
          companyName: opp.companyName || "Unknown company",
          opp,
          contact,
          contacted: Boolean(
            opp.lastContactAt ||
              opp.touchCount > 0 ||
              (opp.pipelineStage && opp.pipelineStage !== "new"),
          ),
          draft: existing || null,
          estimate,
          subject: existing?.subject || "",
          content: existing?.content || "",
          edited: false,
          include: Boolean(existing || estimate?.logId),
          error,
          sent: false,
          loading: false,
        };
      });

      if (cancelled || requestId !== requestRef.current) return;
      setItems(
        results.map((result, index) =>
          result.status === "fulfilled"
            ? result.value
            : {
                ...initial[index],
                loading: false,
                include: false,
                error: errorMessage(result.reason),
              },
        ),
      );
      setPhase("review");
    })();

    return () => {
      cancelled = true;
    };
    // `api` is a stable module singleton; selected is snapshotted by the parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selected]);

  const patchItem = (id, patch) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    );
  };

  const generationTargets = items.filter(
    (item) => !item.draft && item.include && item.estimate?.logId && !item.sent,
  );
  const sendTargets = items.filter(
    (item) => item.draft && item.include && !item.sent,
  );
  const blockedCount = items.filter(
    (item) => !item.draft && !item.estimate?.logId && !item.loading,
  ).length;
  const estimatedTotal = generationTargets.reduce(
    (sum, item) => sum + Number(item.estimate?.estimatedCostGbp || 0),
    0,
  );

  const handleGenerate = async () => {
    if (generationTargets.length === 0) return;
    const startedAt = Date.now();
    setPhase("generating");
    const targets = generationTargets;
    const results = await mapLimit(targets, 3, async (item) => {
      const res = await api.prepareOutreachDraft(
        item.id,
        "outreach_email",
        item.estimate.logId,
      );
      if (!res?.draft) throw new Error("Draft was not returned");
      return res.draft;
    });

    const created = {};
    const failed = {};
    results.forEach((result, index) => {
      const target = targets[index];
      if (result.status === "fulfilled") {
        created[target.id] = result.value;
      } else {
        failed[target.id] = errorMessage(result.reason);
      }
    });

    setItems((prev) =>
      prev.map((item) => {
        if (created[item.id]) {
          const draft = created[item.id];
          return {
            ...item,
            draft,
            subject: draft.subject || "",
            content: draft.content || "",
            edited: false,
            error: null,
            include: true,
          };
        }
        if (failed[item.id]) return { ...item, error: failed[item.id] };
        return item;
      }),
    );

    const createdIds = Object.keys(created);
    if (createdIds.length > 0) onDraftsChanged?.(createdIds);
    await waitForProgress(startedAt);
    setPhase("review");
  };

  const handleSend = async () => {
    if (sendTargets.length === 0) return;
    const startedAt = Date.now();
    setPhase("sending");
    const targets = sendTargets;

    // Persist any inline edits first. Doing this for every target (not just
    // ones the UI marked dirty) makes the server copy authoritative and means
    // a queued edit can never be silently dropped at send time.
    const saved = await mapLimit(targets, CONCURRENCY, (item) =>
      api.updateOutreachDraft(item.draft.id, {
        subject: item.subject,
        content: item.content,
      }),
    );

    const sendIds = [];
    const saveFailures = {};
    saved.forEach((result, index) => {
      const target = targets[index];
      if (result.status === "fulfilled") sendIds.push(target.draft.id);
      else saveFailures[target.id] = errorMessage(result.reason);
    });

    if (Object.keys(saveFailures).length > 0) {
      setItems((prev) =>
        prev.map((item) =>
          saveFailures[item.id]
            ? { ...item, error: saveFailures[item.id] }
            : item,
        ),
      );
    }

    if (sendIds.length === 0) {
      await waitForProgress(startedAt);
      setPhase("review");
      return;
    }

    try {
      const res = await api.bulkApproveAndSendOutreach(sendIds);
      const summaryData = res?.summary || {
        totalRequested: sendIds.length,
        sent: 0,
        failed: sendIds.length,
        results: [],
      };
      const byDraftId = {};
      (summaryData.results || []).forEach((result) => {
        byDraftId[result.draftId] = result;
      });

      setItems((prev) =>
        prev.map((item) => {
          const result = item.draft ? byDraftId[item.draft.id] : null;
          if (!result) return item;
          return result.success
            ? { ...item, sent: true, error: null, include: true }
            : { ...item, error: result.message || "Failed to send", sent: false };
        }),
      );
      setSummary(summaryData);
      await waitForProgress(startedAt);
      setPhase("done");
      onDraftsChanged?.(targets.map((item) => item.id));
      onSent?.(summaryData);
    } catch (err) {
      setItems((prev) =>
        prev.map((item) =>
          item.include && item.draft
            ? { ...item, error: errorMessage(err) }
            : item,
        ),
      );
      await waitForProgress(startedAt);
      setPhase("review");
    }
  };

  const busy = phase === "loading" || phase === "generating" || phase === "sending";
  const progressLabel =
    phase === "generating"
      ? `Generating ${generationTargets.length} draft${
          generationTargets.length === 1 ? "" : "s"
        }…`
      : phase === "sending"
        ? `Sending ${sendTargets.length} email${
            sendTargets.length === 1 ? "" : "s"
          }…`
        : "Preparing outreach…";

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !busy && onClose?.()}>
      <DialogContent
        className="bulk-dialog"
        title="Bulk outreach"
        description="Prepare and send personalised emails for the selected companies."
      >
        <style>{BULK_STYLES}</style>

        {summary && (
          <div className="bulk-summary" role="status">
            <span>
              <b>{summary.sent}</b> sent
            </span>
            <span>
              <b>{summary.failed}</b> failed
            </span>
            <span>
              <b>{summary.totalRequested}</b> requested
            </span>
          </div>
        )}

        {busy && (
          <div className="bulk-progress" role="status" aria-live="polite">
            <div className="bulk-progress-row">
              <span className="bulk-progress-dot" aria-hidden="true" />
              <span>{progressLabel}</span>
            </div>
            <div className="bulk-progress-track" aria-hidden="true">
              <span className="bulk-progress-fill" />
            </div>
          </div>
        )}

        {phase === "loading" ? (
          <Skeleton lines={3} label={progressLabel} />
        ) : (
          <>
            <p className="bulk-intro">
              {items.length > MAX_BULK
                ? `Showing the first ${MAX_BULK} selected companies.`
                : `${items.length} ${
                    items.length === 1 ? "company" : "companies"
                  } selected. Existing email drafts are reused; missing drafts are generated after you approve the estimated cost. Nothing is sent until you press Send.`}
            </p>

            <div className="bulk-list">
              {items.map((item) => {
                const status = item.sent
                  ? "sent"
                  : item.error && !item.draft
                    ? "blocked"
                    : item.draft
                      ? "ready"
                      : item.estimate
                        ? "estimated"
                        : item.loading
                          ? "loading"
                          : "blocked";
                const statusLabel =
                  status === "sent"
                    ? "Sent"
                    : status === "ready"
                      ? "Draft ready"
                      : status === "estimated"
                        ? "Needs draft"
                        : status === "loading"
                          ? "Loading"
                          : "Needs attention";

                return (
                  <div
                    className={`bulk-item${item.sent ? " sent" : ""}${
                      item.error && !item.sent ? " failed" : ""
                    }`}
                    key={item.id}
                  >
                    <div className="bulk-item-head">
                      <Checkbox
                        aria-label={`Include ${item.companyName}`}
                        checked={item.include}
                        disabled={busy || item.sent || !item.draft && !item.estimate}
                        onCheckedChange={(checked) =>
                          patchItem(item.id, { include: checked === true })
                        }
                      />
                      <div className="bulk-item-title">
                        <div className="bulk-item-name">{item.companyName}</div>
                        <div className="bulk-item-meta">
                          {item.contacted && (
                            <span className="bulk-tag contacted">Contacted</span>
                          )}
                          <span className={`bulk-tag ${status}`}>
                            {statusLabel}
                          </span>
                          {(item.draft?.recipientEmail ||
                            item.contact?.email) && (
                            <span className="bulk-item-recipient">
                              To {item.draft?.recipientEmail || item.contact?.email}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {item.sent ? (
                      <div className="bulk-item-body">
                        <div className="bulk-note">
                          Sent to{" "}
                          {item.draft?.recipientEmail ||
                            item.contact?.email ||
                            "the verified contact"}
                          .
                        </div>
                      </div>
                    ) : item.draft ? (
                      <div className="bulk-item-body">
                        <div>
                          <label
                            className="bulk-field-label"
                            htmlFor={`bulk-subject-${item.id}`}
                          >
                            Subject
                          </label>
                          <Input
                            id={`bulk-subject-${item.id}`}
                            label="Subject"
                            hideLabel
                            value={item.subject}
                            disabled={busy}
                            onChange={(event) => {
                              patchItem(item.id, {
                                subject: event.target.value,
                                edited: true,
                              });
                            }}
                          />
                        </div>
                        <div>
                          <label
                            className="bulk-field-label"
                            htmlFor={`bulk-body-${item.id}`}
                          >
                            Email body
                          </label>
                          <Textarea
                            id={`bulk-body-${item.id}`}
                            label="Email body"
                            hideLabel
                            rows={7}
                            value={item.content}
                            disabled={busy}
                            onChange={(event) => {
                              patchItem(item.id, {
                                content: event.target.value,
                                edited: true,
                              });
                            }}
                          />
                        </div>
                      </div>
                    ) : item.estimate ? (
                      <div className="bulk-item-body">
                        <div className="bulk-estimate">
                          <span>No email draft yet.</span>
                          <span>
                            Generating one costs about{" "}
                            <b>{money(item.estimate.estimatedCostGbp)}</b>.
                          </span>
                        </div>
                      </div>
                    ) : (
                      !item.loading && (
                        <div className="bulk-item-body">
                          <div className="bulk-note">
                            No draft is available for this company yet.
                          </div>
                        </div>
                      )
                    )}

                    {item.error && (
                      <div className="bulk-item-body">
                        <div className="bulk-error">{item.error}</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="bulk-foot">
              <span className="bulk-foot-note">
                {phase === "done"
                  ? "Review the results above, then close this window."
                  : generationTargets.length > 0
                    ? `${generationTargets.length} draft${
                        generationTargets.length === 1 ? "" : "s"
                      } to generate · est. ${money(estimatedTotal)}${
                        blockedCount > 0
                          ? ` · ${blockedCount} need contact details`
                          : ""
                      }`
                    : `${sendTargets.length} email${
                        sendTargets.length === 1 ? "" : "s"
                      } ready to send${
                        blockedCount > 0
                          ? ` · ${blockedCount} need contact details`
                          : ""
                      }`}
              </span>
              <div className="bulk-foot-actions">
                <Button
                  type="button"
                  variant="ghost"
                  size="md"
                  onClick={onClose}
                  disabled={busy}
                >
                  {phase === "done" ? "Done" : "Cancel"}
                </Button>
                {phase === "done" ? null : generationTargets.length > 0 ? (
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    onClick={handleGenerate}
                    loading={phase === "generating"}
                    disabled={phase === "sending"}
                  >
                    {phase === "generating"
                      ? "Generating…"
                      : `Generate ${generationTargets.length} draft${
                          generationTargets.length === 1 ? "" : "s"
                        } · ${money(estimatedTotal)}`}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    onClick={handleSend}
                    loading={phase === "sending"}
                    disabled={phase === "generating" || sendTargets.length === 0}
                  >
                    {phase === "sending"
                      ? "Sending…"
                      : `Send ${sendTargets.length} email${
                          sendTargets.length === 1 ? "" : "s"
                        }`}
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
